import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/shared/components/ui/tabs";
import { Badge } from "@/shared/components/ui/badge";
import { HelpCircle, MessageCircle, Send, Phone, Clock, CheckCircle2, Loader2 } from "lucide-react";
import { useToast } from "@/shared/hooks/use-toast";
import { apiClient } from "@/shared/services/api";

interface SupportTicket {
  id: string;
  ticket_id: string;
  subject: string;
  category: string;
  status: string;
  created_at: string;
  updated_at: string;
  last_message?: string;
}

const SupportPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    subject: "",
    category: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [activeTab, setActiveTab] = useState("new");
  const { toast } = useToast();

  // Fetch user's support tickets
  const fetchTickets = async () => {
    setLoadingTickets(true);
    try {
      const response = await apiClient.get<{
        ok: boolean;
        tickets?: SupportTicket[];
        error?: string;
      }>('/support/tickets');

      if (response.ok && response.tickets) {
        setTickets(response.tickets);
      }
    } catch (error) {
      console.error('Error fetching support tickets:', error);
    } finally {
      setLoadingTickets(false);
    }
  };

  // Load tickets when switching to "My Tickets" tab
  useEffect(() => {
    if (activeTab === "tickets") {
      fetchTickets();
    }
  }, [activeTab]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.subject || !formData.category || !formData.message) {
      toast({
        title: "Required Fields Missing",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      console.log('[SupportPage] Submitting support request');

      // Use backend API for support submission (saves to database)
      const response = await apiClient.post<{
        ok: boolean;
        ticket?: { ticket_id: string; id: string };
        error?: string;
      }>('/support/tickets', {
        category: formData.category,
        subject: formData.subject,
        message: formData.message
      });

      if (!response.ok || !response.ticket) {
        throw new Error(response.error || 'Failed to create support ticket');
      }

      console.log('[SupportPage] Support request submitted successfully, ticket:', response.ticket.ticket_id);

      toast({
        title: "Ticket Created!",
        description: `Your support request has been submitted. Ticket ID: ${response.ticket.ticket_id}`,
      });
      
      // Reset form
      setFormData({
        subject: "",
        category: "",
        message: ""
      });

      // Redirect to messages page after a short delay
      setTimeout(() => {
        navigate('/messages');
      }, 2000);
    } catch (error) {
      console.error('=== SUPPORT SUBMISSION ERROR ===');
      console.error('Error details:', error);
      toast({
        title: "Error",
        description: "Failed to submit support request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-primary mb-2">Support Center</h1>
          <p className="text-muted-foreground">We're here to help you with any questions or issues</p>
          <div className="w-16 h-1 bg-primary mx-auto rounded-full mt-4"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-10 gap-8">
          {/* Support Request Form / Tickets - 70% */}
          <div className="lg:col-span-7">
            <Card className="h-full shadow-lg border-0 bg-card/80 backdrop-blur-sm">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <CardHeader>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="new">New Ticket</TabsTrigger>
                    <TabsTrigger value="tickets">My Tickets</TabsTrigger>
                  </TabsList>
                </CardHeader>

                {/* New Ticket Form */}
                <TabsContent value="new">
                  <CardContent className="pt-4">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="category" className="text-sm font-medium text-left block">
                      Category *
                    </Label>
                    <Select onValueChange={(value) => handleInputChange("category", value)}>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="technical">Technical Issue</SelectItem>
                        <SelectItem value="billing">Billing & Payment</SelectItem>
                        <SelectItem value="feature">Feature Request</SelectItem>
                        <SelectItem value="account">Account Management</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject" className="text-sm font-medium">
                      Subject *
                    </Label>
                    <Input
                      id="subject"
                      placeholder="Brief description of your issue"
                      value={formData.subject}
                      onChange={(e) => handleInputChange("subject", e.target.value)}
                      className="h-12"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-sm font-medium">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      placeholder="Please provide detailed information about your issue or question"
                      value={formData.message}
                      onChange={(e) => handleInputChange("message", e.target.value)}
                      rows={8}
                      className="resize-none"
                      required
                      autoCorrect="on"
                      spellCheck="true"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-medium"
                    disabled={loading}
                  >
                    {loading ? (
                      "Submitting..."
                    ) : (
                      <>
                        Submit Request
                        <Send className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
                  </CardContent>
                </TabsContent>

                {/* My Tickets List */}
                <TabsContent value="tickets">
                  <CardContent className="pt-4">
                    {loadingTickets ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      </div>
                    ) : tickets.length === 0 ? (
                      <div className="text-center py-12">
                        <MessageCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <h3 className="font-semibold text-lg mb-2">No Support Tickets</h3>
                        <p className="text-muted-foreground mb-4">
                          You haven't submitted any support requests yet.
                        </p>
                        <Button onClick={() => setActiveTab("new")}>
                          Create New Ticket
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {tickets.map((ticket) => (
                          <Card key={ticket.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/messages?ticket=${ticket.ticket_id}`)}>
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 space-y-2">
                                  <div className="flex items-center gap-2">
                                    <h3 className="font-semibold">{ticket.subject}</h3>
                                    <Badge variant={
                                      ticket.status === 'resolved' ? 'default' :
                                      ticket.status === 'in_progress' ? 'secondary' :
                                      'outline'
                                    } className="text-xs">
                                      {ticket.status === 'open' ? 'Open' :
                                       ticket.status === 'in_progress' ? 'In Progress' :
                                       ticket.status === 'resolved' ? 'Resolved' :
                                       ticket.status === 'closed' ? 'Closed' : ticket.status}
                                    </Badge>
                                  </div>
                                  <p className="text-sm text-muted-foreground">
                                    Category: {ticket.category}
                                  </p>
                                  {ticket.last_message && (
                                    <p className="text-sm text-muted-foreground line-clamp-2">
                                      {ticket.last_message}
                                    </p>
                                  )}
                                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                    <span className="flex items-center gap-1">
                                      <Clock className="h-3 w-3" />
                                      Created {new Date(ticket.created_at).toLocaleDateString()}
                                    </span>
                                    <span>Ticket ID: {ticket.ticket_id}</span>
                                  </div>
                                </div>
                                <Button variant="ghost" size="sm">
                                  View
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </TabsContent>
              </Tabs>
            </Card>
          </div>

          {/* Quick Help Section - 30% */}
          <div className="lg:col-span-3">
            <Card className="h-full shadow-lg border-0 bg-card/80 backdrop-blur-sm">
              <CardHeader>
                <div>
                  <div>
                    <CardTitle className="text-lg">Quick Help Available</CardTitle>
                    <CardDescription>
                      Get instant answers to common questions
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* AI Chatbot Promotion */}
                <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                  <h4 className="font-semibold text-primary mb-2 text-center">wlkthru Bot Assistant</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Get instant answers to common questions using our chatbot.
                  </p>
                  <Button variant="outline" size="sm" className="w-full">
                    Chat with wlkthru Bot
                  </Button>
                </div>

                {/* FAQ Section */}
                <div className="space-y-3">
                  <h4 className="font-semibold">Frequently Asked Questions</h4>
                  <div className="space-y-2">
                     <FAQItem
                       question="How do I change my password?"
                       answer="To change your password, go to your Profile page and click on the Security tab where you'll find the 'Change Password' option."
                       linkText="Go to Profile - Security"
                       linkUrl="/profile"
                     />
                     
                     <FAQItem
                       question="Where can I manage my billing and subscription?"
                       answer="For billing information, visit your Profile page and click on the Billing tab to view invoices, payment methods, subscription details, and manage your account billing."
                       linkText="Go to Profile - Billing"
                       linkUrl="/profile?tab=billing"
                     />

                     <FAQItem
                       question="How do I manage my team and users?"
                       answer="Manage your team members and user access levels from the Users page. You can add, edit, and remove users from your organization."
                       linkText="Go to Users"
                       linkUrl="/users"
                     />

                     <FAQItem
                       question="How do I schedule or manage calls?"
                       answer="You can schedule calls from the Calls page where you can create new appointments, view upcoming calls, and manage your call history."
                       linkText="Go to Calls"
                       linkUrl="/calls"
                     />
                  </div>
                </div>

              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

// FAQ Item Component with Modal and Navigation
const FAQItem = ({ 
  question, 
  answer, 
  linkText, 
  linkUrl 
}: { 
  question: string; 
  answer: string; 
  linkText?: string; 
  linkUrl?: string; 
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleNavigate = () => {
    if (linkUrl) {
      window.location.href = linkUrl;
    }
    setIsOpen(false);
  };

  return (
    <>
      <div 
        className="p-3 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors"
        onClick={() => setIsOpen(true)}
      >
        <p className="text-sm font-medium">{question}</p>
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-left">{question}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-muted-foreground mb-4">{answer}</p>
          </div>
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Back
            </Button>
            {linkText && linkUrl && (
              <Button onClick={handleNavigate} className="ml-2">
                {linkText}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SupportPage;
